/**
 * StepRoadmap - Vertical list of steps with dashed connector lines.
 * Renders StepItem for each step and a dotted line between items.
 * Parent supplies translated labels and progress; this component only handles layout and state.
 */
import React from 'react';
import StepItem, { StepState } from './StepItem';
import './StepRoadmap.css';

export interface StepRoadmapStep {
  id: string;
  label: string;
  completed: number;
  total: number;
  badgeCount?: number;
}

export interface StepRoadmapProps {
  /** Steps in order; each has id, translated label, completed count, total */
  steps: StepRoadmapStep[];
  /** Index of the current (in-progress) step; first step where completed < total */
  currentIndex: number;
  /** Called when a step is clicked (e.g. navigate to that step) */
  onStepClick?: (index: number) => void;
}

const StepRoadmap: React.FC<StepRoadmapProps> = ({
  steps,
  currentIndex,
  onStepClick,
}) => {
  const getState = (index: number, completed: number, total: number): StepState => {
    if (completed >= total) return 'completed';
    if (index === currentIndex) return 'current';
    return 'pending';
  };

  return (
    <div className="step-roadmap" role="list" aria-label="Document submission steps">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const state = getState(index, step.completed, step.total);

        return (
          <React.Fragment key={step.id}>
            <div className="step-roadmap__item" role="listitem">
              <StepItem
                label={step.label}
                completed={step.completed}
                total={step.total}
                state={state}
                badgeCount={step.badgeCount}
                onClick={onStepClick ? () => onStepClick(index) : undefined}
              />
            </div>
            {!isLast && <div className="step-roadmap__connector" aria-hidden />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepRoadmap;
